/* Customer portal — Quotes: request + list + accept. */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HelpCircle, CheckCircle2, XCircle, Clock } from "lucide-react";
import { toast } from "sonner";

const statusStyle = {
  pending:  "bg-amber-100 text-amber-700 border-amber-300",
  quoted:   "bg-primary-orange/10 text-primary-orange border-primary-orange",
  accepted: "bg-emerald-100 text-emerald-700 border-emerald-300",
  converted:"bg-emerald-100 text-emerald-700 border-emerald-300",
  rejected: "bg-neutral-200 text-neutral-500 border-neutral-300",
};

export default function PortalQuotes() {
  const { user } = usePortalAuth();
  const nav = useNavigate();
  const [mixes, setMixes] = useState([]);
  const [list, setList] = useState([]);
  const [form, setForm] = useState({
    mix_id: "", quantity_m3: 3, requested_date: new Date().toISOString().slice(0, 10),
    time_required: "09:00", site_name: "", site_address: "", delivery_km: "", notes: "",
  });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [m, q] = await Promise.all([api.get("/portal/mixes"), api.get("/portal/quotes")]);
      setMixes(m.data);
      setList(q.data);
    } catch (e) { console.warn("portal quotes load failed", e); }
  };
  useEffect(() => { load(); }, []);

  const set = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });
  const selectedMix = useMemo(() => mixes.find((m) => m.id === form.mix_id), [mixes, form.mix_id]);

  const estimate = useMemo(() => {
    if (!selectedMix || !form.quantity_m3) return null;
    const qty = Number(form.quantity_m3) || 0;
    const price = Number(selectedMix.price_per_m3);
    const base = qty * price;
    const short = qty < 3 ? Math.max(0, 3 - qty) * 70 : 0;
    const km = Number(form.delivery_km) || 0;
    const delivery = Math.max(0, km - 25) * 2;
    const subtotal = base + short + delivery;
    const gst = subtotal * 0.10;
    return { base, short, delivery, subtotal, gst, total: subtotal + gst };
  }, [selectedMix, form.quantity_m3, form.delivery_km]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.mix_id) return toast.error("Choose a mix");
    setBusy(true);
    try {
      await api.post("/portal/quotes", {
        ...form,
        quantity_m3: Number(form.quantity_m3),
        delivery_km: form.delivery_km ? Number(form.delivery_km) : null,
      });
      toast.success("Quote request submitted");
      setForm({ ...form, notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Quote request failed");
    } finally { setBusy(false); }
  };

  const accept = async (id) => {
    try {
      await api.post(`/portal/quotes/${id}/accept`);
      toast.success("Quote accepted — order scheduled");
      nav("/portal/jobs");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cannot accept yet");
    }
  };

  return (
    <div className="space-y-8" data-testid="portal-quotes">
      <div>
        <div className="text-[10px] tracking-[0.28em] uppercase text-neutral-500">Quotes</div>
        <h1 className="text-4xl font-black">Request a quote</h1>
        <p className="text-neutral-500 text-sm mt-1">Instant estimate below — dispatch will confirm firm pricing before you accept.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-neutral-200 p-6 lg:p-8 space-y-5 shadow-sm max-w-3xl">
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Mix</Label>
            <Select value={form.mix_id} onValueChange={set("mix_id")}>
              <SelectTrigger data-testid="quote-mix" className="rounded-none border-neutral-300"><SelectValue placeholder="Choose a mix" /></SelectTrigger>
              <SelectContent>{mixes.map((m) => <SelectItem key={m.id} value={m.id}>{m.code} · {m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Quantity (m³)</Label>
            <Input data-testid="quote-qty" type="number" step="0.5" value={form.quantity_m3} onChange={set("quantity_m3")} className="rounded-none border-neutral-300 mono" />
          </div>
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Preferred date</Label>
            <Input data-testid="quote-date" type="date" value={form.requested_date} onChange={set("requested_date")} className="rounded-none border-neutral-300 mono" />
          </div>
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Time on site</Label>
            <Input data-testid="quote-time" type="time" value={form.time_required} onChange={set("time_required")} className="rounded-none border-neutral-300 mono" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Site address</Label>
            <Input data-testid="quote-site" value={form.site_address} onChange={set("site_address")} className="rounded-none border-neutral-300" />
          </div>
          <div>
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Distance from plant (km, optional)</Label>
            <Input data-testid="quote-km" type="number" value={form.delivery_km} onChange={set("delivery_km")} className="rounded-none border-neutral-300 mono" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-[11px] tracking-widest uppercase text-neutral-500">Notes</Label>
            <Textarea data-testid="quote-notes" rows={2} value={form.notes} onChange={set("notes")} className="rounded-none border-neutral-300" />
          </div>
        </div>

        {estimate && (
          <div className="border border-neutral-200 bg-neutral-50 p-4" data-testid="quote-estimate">
            <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-2">Instant estimate</div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm">
              <div><div className="text-neutral-500 text-xs">Concrete</div><div className="mono font-bold">${estimate.base.toFixed(2)}</div></div>
              <div><div className="text-neutral-500 text-xs">Min-load</div><div className="mono font-bold">${estimate.short.toFixed(2)}</div></div>
              <div><div className="text-neutral-500 text-xs">Delivery</div><div className="mono font-bold">${estimate.delivery.toFixed(2)}</div></div>
              <div><div className="text-neutral-500 text-xs">GST 10%</div><div className="mono font-bold">${estimate.gst.toFixed(2)}</div></div>
              <div><div className="text-neutral-500 text-xs">Total</div><div className="mono font-black text-primary-orange text-lg">${estimate.total.toFixed(2)}</div></div>
            </div>
          </div>
        )}

        <div className="flex justify-end">
          <Button data-testid="quote-submit" type="submit" disabled={busy} className="rounded-none bg-primary-orange text-black hover:bg-[rgb(var(--primary-hover))] uppercase tracking-widest text-sm font-bold px-6 py-3">
            <HelpCircle className="w-4 h-4 mr-2" />
            {busy ? "Submitting…" : "Submit request"}
          </Button>
        </div>
      </form>

      <div>
        <h2 className="text-[11px] uppercase tracking-widest text-neutral-500 mb-3">My quote requests</h2>
        <div className="bg-white border border-neutral-200 divide-y divide-neutral-100">
          {list.length === 0 && <div className="p-6 text-neutral-500 text-sm text-center">No quotes yet.</div>}
          {list.map((q) => (
            <div key={q.id} className="p-4 flex items-center gap-4 flex-wrap" data-testid={`portal-quote-${q.id}`}>
              <div className="w-10 h-10 bg-primary-orange/10 text-primary-orange flex items-center justify-center rounded shrink-0">
                <Clock className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">{q.mix_code} · <span className="mono">{q.quantity_m3} m³</span></div>
                <div className="text-xs text-neutral-500">Requested {q.requested_date} · {q.site_address}</div>
              </div>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 border ${statusStyle[q.status] || ""}`}>{q.status}</span>
              <div className="mono font-black">${Number(q.total).toFixed(2)}</div>
              {(q.status === "quoted" || (user?.customer_type === "account" && q.status === "pending")) && (
                <Button data-testid={`quote-accept-${q.id}`} onClick={() => accept(q.id)} className="rounded-none bg-emerald-600 text-white hover:bg-emerald-700 uppercase tracking-widest text-xs font-bold">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1.5" /> Accept & schedule
                </Button>
              )}
              {q.status === "converted" && <span className="text-emerald-600 text-xs font-bold uppercase tracking-widest">Scheduled ✓</span>}
              {q.status === "rejected" && <span className="text-neutral-400 text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />Declined</span>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
